import { supabase } from '@/integrations/supabase/client';

export interface TranslationRequest {
  text: string;
  targetLanguage: 'english' | 'vietnamese';
}

export interface TranslationResponse {
  translatedText: string;
  originalText: string;
  targetLanguage: string;
}

export interface VerbIdentificationRequest {
  sentence: string;
}

export interface IdentifiedVerb {
  german: string;
  english: string;
  vietnamese: string;
}

export interface VerbIdentificationResponse {
  verbs: IdentifiedVerb[];
  sentence: string;
}

export interface ParsedQuestion {
  questionNumber: number;
  germanQuestion: string;
  answers: string[];
}

export interface QuestionParsingRequest {
  text: string;
}

export interface QuestionParsingResponse {
  questions: ParsedQuestion[];
}

export interface QuestionTranslationRequest {
  germanQuestion: string;
  germanAnswers: string[];
  targetLanguage: 'english' | 'vietnamese';
}

export interface QuestionTranslationResponse {
  translatedQuestion: string;
  translatedAnswers: string[];
  originalQuestion: string;
  originalAnswers: string[];
  targetLanguage: string;
}

// Helper function to make authenticated requests to Edge Functions
const callEdgeFunction = async (
  functionName: string,
  payload: Record<string, unknown>,
  userId?: string
) => {
  // For Clerk auth, we need to pass the user ID in headers
  const headers: Record<string, string> = {};

  if (userId) {
    headers['x-clerk-user-id'] = userId;
  }

  const { data, error } = await supabase.functions.invoke(functionName, {
    body: payload,
    headers,
  });

  if (error) {
    throw new Error(`Edge Function ${functionName} error: ${error.message}`);
  }

  return data;
};

export const translateText = async (
  { text, targetLanguage }: TranslationRequest,
  userId?: string
): Promise<TranslationResponse> => {
  try {
    return await callEdgeFunction(
      'translate-text',
      { text, targetLanguage },
      userId
    );
  } catch (error) {
    console.error(`Translation error for ${targetLanguage}:`, error);

    // Fallback error message
    const fallbackMessage =
      targetLanguage === 'english'
        ? '[Translation failed - please try again]'
        : '[Dịch thất bại - vui lòng thử lại]';

    return {
      translatedText: fallbackMessage,
      originalText: text,
      targetLanguage: targetLanguage === 'english' ? 'English' : 'Vietnamese',
    };
  }
};

// Characters that can legally appear AFTER terminal punctuation while still
// belonging to the same sentence (closing quotes, brackets, parentheses).
// Includes ASCII, curly/typographic quotes, German „ " and guillemets « » ‹ ›.
const TRAILING_CLOSERS =
  '"\'\u2018\u2019\u201A\u201C\u201D\u201E\u00AB\u00BB\u2039\u203A)\\]}>';
const TRAILING_CLOSERS_RE = new RegExp(`[${TRAILING_CLOSERS}]`);
// Same set as above plus whitespace, used to peel trailing decoration off a
// sentence so we can inspect the *real* last character.
const TRAILING_DECORATION_RE = new RegExp(`[\\s${TRAILING_CLOSERS}]+$`);

// Function to break German text into sentences (client-side only, no AI needed)
export const breakIntoSentences = (text: string): string[] => {
  // Clean the text
  const cleaned = text.trim();

  // Split on sentence endings and newlines, but be careful with abbreviations and numbered items
  const sentences = cleaned
    // First split on newlines to handle line breaks
    .split(/[\n\r]+/)
    .flatMap(line => {
      // For each line, split on sentence endings but avoid false positives
      const parts: string[] = [];
      let current = '';

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const prevChar = line[i - 1];

        current += char;

        // Check if this is a sentence ending punctuation
        if (/[.!?]/.test(char)) {
          // Look ahead to see what comes after, skipping any closing quotes/brackets
          let lookAheadIndex = i + 1;
          while (
            lookAheadIndex < line.length &&
            TRAILING_CLOSERS_RE.test(line[lookAheadIndex])
          ) {
            current += line[lookAheadIndex];
            lookAheadIndex++;
          }

          // Update our position if we consumed quote characters
          if (lookAheadIndex > i + 1) {
            i = lookAheadIndex - 1; // -1 because the loop will increment
          }

          const afterPunct = line.slice(lookAheadIndex).trim();

          // Don't split if:
          // 1. Previous char is a digit and next word starts with uppercase (like "13. Jahrhundert")
          // 2. This looks like an abbreviation (single letter + period)
          // 3. Next character is lowercase (likely continuation)
          const isNumberedItem =
            /\d/.test(prevChar) && /^\s*[A-ZÄÖÜ]/.test(afterPunct);
          const isAbbreviation =
            current.match(/\b[A-ZÄÖÜ]\.$/) ||
            current.match(/\b(?:Dr|Mr|Mrs|Prof|etc|usw|z\.B|u\.a|bzw|ggf)\.$/i);
          const isContinuation =
            lookAheadIndex < line.length &&
            /^[a-zäöüß]/.test(line[lookAheadIndex]);

          if (
            !isNumberedItem &&
            !isAbbreviation &&
            !isContinuation &&
            afterPunct.length > 0
          ) {
            parts.push(current.trim());
            current = '';
          }
        }
      }

      // Add any remaining text
      if (current.trim()) {
        parts.push(current.trim());
      }

      return parts;
    })
    .filter(s => s.length > 0)
    .map(s => {
      // Add a period only if the sentence is missing terminal punctuation.
      // Strip trailing closing quotes/brackets/whitespace before checking so
      // that sentences like `Er sagte: "Hallo."` are recognised as already
      // terminated (otherwise we'd produce `Er sagte: "Hallo.".`).
      const stripped = s.replace(TRAILING_DECORATION_RE, '');
      if (!/[.!?…]$/u.test(stripped)) {
        return s + '.';
      }
      return s;
    });

  // Filter out fragments that are only whitespace/punctuation/quotes (no
  // letters or digits). This protects against stray "sentences" like `."` or
  // `...` slipping through.
  return sentences.filter(s => /[\p{L}\p{N}]/u.test(s));
};

export const identifyDifficultVerbs = async (
  { sentence }: VerbIdentificationRequest,
  userId?: string
): Promise<VerbIdentificationResponse> => {
  // Intentionally NOT swallowing errors here. The previous behaviour returned
  // `{ verbs: [] }` on any failure, which silently let articles get saved with
  // no difficult words and left the `sentence_verbs` table empty. Let the
  // caller (`handleIdentifyVerbs`) decide how to surface the failure.
  return await callEdgeFunction('identify-verbs', { sentence }, userId);
};

export const parseQuestions = async (
  { text }: QuestionParsingRequest,
  userId?: string
): Promise<QuestionParsingResponse> => {
  try {
    return await callEdgeFunction('parse-questions', { text }, userId);
  } catch (error) {
    console.error('Question parsing error:', error);
    return { questions: [] };
  }
};

export const translateQuestion = async (
  { germanQuestion, germanAnswers, targetLanguage }: QuestionTranslationRequest,
  userId?: string
): Promise<QuestionTranslationResponse> => {
  try {
    return await callEdgeFunction(
      'translate-question',
      {
        germanQuestion,
        germanAnswers,
        targetLanguage,
      },
      userId
    );
  } catch (error) {
    console.error(`Question translation error for ${targetLanguage}:`, error);

    // Fallback error message
    const fallbackMessage =
      targetLanguage === 'english'
        ? '[Translation failed - please try again]'
        : '[Dịch thất bại - vui lòng thử lại]';

    return {
      translatedQuestion: fallbackMessage,
      translatedAnswers: germanAnswers.map(() => fallbackMessage),
      originalQuestion: germanQuestion,
      originalAnswers: germanAnswers,
      targetLanguage: targetLanguage === 'english' ? 'English' : 'Vietnamese',
    };
  }
};
